import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, MenuItem, Skeleton, TextField, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Download from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import DirectoryPage from '../components/directories/DirectoryPage';
import DirectoryPagination from '../components/directories/DirectoryPagination';
import DirectorySavedSearches, { DirectoryColumns, useDirectoryColumns } from '../components/directories/DirectoryPreferences';
import PlayerComparisonWorkspace from '../components/directories/PlayerComparisonWorkspace';
import PlayerDiscoveryControls from '../components/directories/PlayerDiscoveryControls';
import PlayerSimilarity from '../components/directories/PlayerSimilarity';
import PlayerGlobalSearch from '../components/directories/PlayerGlobalSearch';
import PlayerRankingResults from '../components/directories/PlayerRankingResults';
import Filters from '../components/Filters';
import FollowPlayerButton from '../components/scouting/FollowPlayerButton';
import { LicensePreviewNotice } from '../components/license/LicenseGate';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { useJugadorEstadisticas, useJugadorEstadisticasByCompeticion } from '../hooks/queries';
import { playerKey, playerMetric, playerName } from '../utils/playerRanking';
import { DISCOVERY_METRICS, discoveryMetric, DISCOVERY_QUERY_KEYS, eligibleDiscoverySample,
  matchesDiscoveryFilters, playerDiscoveryUrl, playerPosition, readDiscoveryFilters } from '../utils/playerDiscovery';
import usePageTitle from '../hooks/usePageTitle';
import usePageTour from '../hooks/usePageTour';

const METRICS = [
  ['partidosJugados', 'matchesPlayed'], ['minutosJugados', 'minutes'],
  ['golesTotales', 'goals'], ['minutosPorGol', 'minutesPerGoal'],
  ['edad', 'age'], ['titularidades', 'starters'], ['convocatorias', 'callups'],
  ['golesPenaltis', 'penalties'], ['golesPorPartido', 'goalsPerMatch'],
  ['amarillasTotales', 'yellow'], ['segundasAmarillasTotales', 'secondYellow'],
  ['rojasTotales', 'red'], ['puntosDisciplinarios', 'Pts.'],
  ...DISCOVERY_METRICS.map(({ id }) => [`d_${id}`, 'discovery']),
];
const CATEGORY_METRICS = {
  all: ['partidosJugados', 'minutosJugados', 'golesTotales', 'minutosPorGol'],
  goles: ['partidosJugados', 'golesTotales', 'golesPenaltis', 'minutosPorGol', 'golesPorPartido'],
  minutosJugados: ['partidosJugados', 'minutosJugados', 'titularidades', 'convocatorias'],
  puntosDisciplinarios: ['partidosJugados', 'amarillasTotales', 'segundasAmarillasTotales', 'rojasTotales', 'puntosDisciplinarios'],
};

export default function RankingJugadoresPageModern({ similarityPage = false, comparisonPage = false }) {
  const { t, i18n } = useTranslation('rankings');
  const { t: d } = useTranslation('directories');
  usePageTitle(t('players.headerTitle'));
  usePageTour('rankingJugadores');
  const { filters: appFilters, setFilters, setDatosJugadores } = useAppContext();
  const { hasPermission } = useAuth();
  const full = hasPermission('analytics.view');
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const showingSimilar = full && similarityPage;
  const showingComparison = full && comparisonPage;
  const globalSearch = full && !similarityPage && !comparisonPage && params.get('searchScope') === 'global';
  const contextSeason = params.get('contextSeason'), contextCompetition = params.get('contextCompetition'),
    contextGroup = params.get('contextGroup');
  const pinnedContext = Boolean(full && contextSeason && contextCompetition && contextGroup);
  const filters = pinnedContext ? { ...appFilters, temporada: contextSeason, competicion: contextCompetition, grupo: contextGroup } : appFilters;
  const comparisonContext = useMemo(() => pinnedContext
    ? { temporada: contextSeason, competicion: contextCompetition, grupo: contextGroup } : appFilters,
  [pinnedContext, contextSeason, contextCompetition, contextGroup, appFilters]);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const mobile = useMediaQuery('(max-width:699px)');
  const query = full ? params.get('q') || '' : '';
  const team = full ? params.get('team') || '' : '';
  const teamId = full ? params.get('teamId') || '' : '';
  const position = full ? params.get('position') || '' : '';
  const minimum = full ? Math.max(0, Number(params.get('minMinutes')) || 0) : 0;
  const category = full && Object.keys(CATEGORY_METRICS).includes(params.get('category')) ? params.get('category') : 'all';
  const categoryMetrics = METRICS.filter(([key]) => CATEGORY_METRICS[category].includes(key));
  const rankingTitle = category === 'all' ? t('players.headerTitle') : t(`players.categoryTitles.${category}`);
  const sort = full && ['nombre', ...METRICS.map(([key]) => key)].includes(params.get('sort')) ? params.get('sort') : 'golesTotales';
  const direction = full && params.get('direction') === 'asc' ? 'asc' : 'desc';
  const cards = mobile || params.get('view') === 'cards';
  const details = params.get('details') === 'all';
  const availableMetrics = METRICS.filter(([key]) => full || !key.startsWith('d_'));
  const [columns, setColumns] = useDirectoryColumns(category === 'all' ? 'players' : `players-${category}`,
    availableMetrics.map(([key]) => key), (details ? availableMetrics : categoryMetrics).map(([key]) => key));
  const shownMetrics = availableMetrics.filter(([key]) => columns.includes(key) || (full && key === sort && key.startsWith('d_')));
  const groupQuery = useJugadorEstadisticas(filters.grupo, filters.temporada, { enabled: Boolean(!showingComparison && !globalSearch && filters.grupo && filters.temporada) });
  const competitionQuery = useJugadorEstadisticasByCompeticion(filters.competicion, filters.temporada, {
    enabled: Boolean(!showingComparison && !globalSearch && filters.competicion && filters.temporada && !filters.grupo),
  });
  const source = useMemo(() => {
    const records = (filters.grupo ? groupQuery.data : filters.competicion ? competitionQuery.data : groupQuery.data || competitionQuery.data) || [];
    return pinnedContext ? records.filter(player => String(player.idTemporada) === contextSeason
      && String(player.idCompeticion) === contextCompetition && String(player.idGrupo) === contextGroup) : records;
  }, [groupQuery.data, competitionQuery.data, filters.grupo, filters.competicion, pinnedContext, contextSeason, contextCompetition, contextGroup]);
  const sources = useMemo(() => [...new Set(source.map(player => player.estadisticasAvanzadas?.fuente).filter(Boolean))].sort(), [source]);
  const discovery = useMemo(() => readDiscoveryFilters(full ? params : new URLSearchParams()), [params, full]);
  const sampleSettings = useMemo(() => ({ ...discovery,
    source: discovery.source || (sources.length === 1 ? sources[0] : ''),
  }), [discovery, sources]);
  const advancedSearch = discoveryMetric(sort.slice(2))?.advanced || discovery.criteria.some(rule => discoveryMetric(rule?.metric)?.advanced);
  const needsSource = full && advancedSearch && !sampleSettings.source;
  const sampleFor = useMemo(() => {
    const cache = new WeakMap();
    return (player, id) => {
      if (!cache.has(player)) cache.set(player, new Map());
      const metrics = cache.get(player);
      if (!metrics.has(id)) {
        const sample = eligibleDiscoverySample(player, id, sampleSettings);
        metrics.set(id, discoveryMetric(id)?.advanced && !sampleSettings.source ? { ...sample, value: null } : sample);
      }
      return metrics.get(id);
    };
  }, [sampleSettings]);
  const metricValue = useCallback((player, key) => key.startsWith('d_')
    ? sampleFor(player, key.slice(2)).value : playerMetric(player, key), [sampleFor]);
  useEffect(() => { if (!showingComparison && !globalSearch) setDatosJugadores(source); },
    [source, setDatosJugadores, showingComparison, globalSearch]);
  const loading = groupQuery.isLoading || competitionQuery.isLoading;
  const error = groupQuery.isError || competitionQuery.isError;
  const retry = () => filters.grupo ? groupQuery.refetch() : filters.competicion ? competitionQuery.refetch() : undefined;
  const update = (changes) => setParams((previous) => {
    const next = new URLSearchParams(previous);
    if (Object.keys(changes).some((key) => ['q', 'team', 'teamId', 'position', 'minMinutes', 'category', 'sort', 'direction', ...DISCOVERY_QUERY_KEYS].includes(key))) next.delete('page');
    Object.entries(changes).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    return next;
  }, { replace: true });
  const players = useMemo(() => source.filter((player) =>
    (!query || `${playerName(player)} ${player.nombreEquipo || player.equipo || ''}`.toLocaleLowerCase(i18n.language).includes(query.toLocaleLowerCase(i18n.language))) &&
    (teamId ? String(player.idEquipo ?? player.codEquipo ?? '') === teamId : (!team || (player.nombreEquipo || player.equipo) === team)) &&
    (!position || (position === '__unknown__' ? !playerPosition(player) : playerPosition(player) === position)) &&
    playerMetric(player, 'minutosJugados') >= minimum &&
    (!full || (!needsSource && matchesDiscoveryFilters(player, { ...discovery,
      source: advancedSearch ? sampleSettings.source : discovery.source }))) &&
    (!sort.startsWith('d_') || metricValue(player, sort) !== null) &&
    (category === 'all' || playerMetric(player, category === 'goles' ? 'golesTotales' : category) > 0)
  ).sort((a, b) => {
    const comparison = sort === 'nombre' ? playerName(a).localeCompare(playerName(b), i18n.language)
      : metricValue(a, sort) - metricValue(b, sort);
    return direction === 'asc' ? comparison : -comparison;
  }), [source, query, team, teamId, position, minimum, category, sort, direction, i18n.language, full,
    needsSource, discovery, sampleSettings.source, advancedSearch, metricValue]);
  const pageSize = 25;
  const page = full ? Math.min(Math.max(1, Math.floor(Number(params.get('page')) || 1)), Math.max(1, Math.ceil(players.length / pageSize))) : 1;
  const visible = full ? players.slice((page - 1) * pageSize, page * pageSize) : players.slice(0, 5);
  const preview = visible.find((player) => playerKey(player) === params.get('player'));
  const selected = preview || visible.find(player => playerKey(player) === selectedPlayerId);
  const selectedId = selected && playerKey(selected);
  useEffect(() => {
    if (mobile && selectedId) {
      const panel = document.getElementById('ranking-selected-player');
      panel?.scrollIntoView?.({ block: 'start' });
      panel?.focus({ preventScroll: true });
    }
  }, [mobile, selectedId]);
  const teams = [...new Set(source.map((player) => player.nombreEquipo || player.equipo).filter(Boolean))].sort();
  const canonicalTeam = teamId ? source.find((player) => String(player.idEquipo ?? player.codEquipo ?? '') === teamId) : null;
  const selectedTeamName = canonicalTeam?.nombreEquipo || canonicalTeam?.equipo || team;
  const positions = [...new Set(source.map(playerPosition).filter(Boolean))].sort();
  const metricLabel = (key, label) => label === 'discovery' ? d(`discovery.metrics.${key.slice(2)}`) : ['yellow', 'secondYellow', 'red'].includes(label) ? d(label)
    : label === 'Pts.' ? label : t(`players.table.${label}`);
  const format = (player, key) => {
    const value = metricValue(player, key);
    if (value === null) return '—';
    return new Intl.NumberFormat(i18n.language, {
      maximumFractionDigits: key.startsWith('d_') ? 2 : key === 'minutosPorGol' ? 1 : key === 'golesPorPartido' ? 2 : 0,
    }).format(value);
  };
  const displayMetric = (player, key) => {
    const sample = key.startsWith('d_') && discoveryMetric(key.slice(2))?.advanced
      ? sampleFor(player, key.slice(2)) : null;
    return <>{format(player, key)}{sample && sample.value !== null && <small className="pd-sample">
      {d('discovery.sample', { matches: sample.matches ?? '—', minutes: sample.minutes ?? '—' })}
    </small>}</>;
  };
  const profileUrl = (player) => `/jugador?${new URLSearchParams({
    id: player.id ?? player.idJugador,
    ...(player.idTemporada || filters.temporada ? { idTemporada: player.idTemporada || filters.temporada } : {}),
  })}`;
  const profileState = (player) => ({ jugador: player, directoryFrom: location.pathname + location.search });
  const showSimilar = player => {
    const next = new URLSearchParams(params);
    const destination = playerDiscoveryUrl(player);
    const context = destination && new URLSearchParams(destination.split('?')[1]);
    context?.forEach((value, key) => next.set(key, value));
    next.set('similar', playerKey(player));
    next.delete('player');
    navigate(`/jugadoresSimilares?${next}`);
  };
  const showComparison = (first, second) => {
    const next = new URLSearchParams(params);
    const contextUrl = playerDiscoveryUrl(first);
    if (contextUrl) new URLSearchParams(contextUrl.split('?')[1]).forEach((value, key) => {
      if (key !== 'similar') next.set(key, value);
    });
    next.delete('similar');
    next.delete('player');
    next.set('compareA', playerKey(first));
    if (second) next.set('compareB', playerKey(second));
    else next.delete('compareB');
    navigate(`/compararJugadores?${next}`);
  };
  const rankingParams = new URLSearchParams(params);
  ['similar', 'similarMode', 'similarPosition', 'similarMinutes', 'similarMatches', 'compareA', 'compareB',
    'compareContextA', 'compareContextB', 'player'].forEach(key => rankingParams.delete(key));
  const sortBy = (key) => full && update({ sort: key, direction: sort === key && direction === 'desc' ? 'asc' : 'desc' });
  const exportExcel = () => {
    if (!full || !players.length) return;
    const data = players.map((player, index) => ({
      [d('rank')]: index + 1, Nombre: player.nombre || '', Apellidos: player.apellidos || '',
      [t('players.table.team')]: player.nombreEquipo || player.equipo || '—',
      [t('players.filters.position.label')]: playerPosition(player) || '—',
      [d('discovery.metricSource')]: player.estadisticasAvanzadas?.fuente || '',
      ...Object.fromEntries(METRICS.map(([key, label]) => [metricLabel(key, label), metricValue(player, key)])),
      ...Object.fromEntries(DISCOVERY_METRICS.filter(metric => metric.advanced).flatMap(({ id }) => [
        [d('discovery.sampleMatchesColumn', { metric: d(`discovery.metrics.${id}`) }), sampleFor(player, id).matches],
        [d('discovery.sampleMinutesColumn', { metric: d(`discovery.metrics.${id}`) }), sampleFor(player, id).minutes],
      ])),
    }));
    const sheet = XLSX.utils.json_to_sheet(data);
    sheet['!cols'] = Object.keys(data[0]).map(() => ({ wch: 20 }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Ranking Jugadores');
    XLSX.writeFile(book, `Ranking_Jugadores_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const follow = (player) => <FollowPlayerButton jugadorId={player.idJugador}
    jugadorNombre={playerName(player)} equipoNombre={player.nombreEquipo || player.equipo} size="small" />;
  const contextControls = <>
    <Filters variant="workspace" onApplyFilters={() => {
      if (pinnedContext) update({ contextSeason: null, contextCompetition: null, contextGroup: null, similar: null,
        compareA: null, compareB: null });
      retry();
    }} />
    {pinnedContext && <p className="dr-muted">{d('discovery.pinnedContext', {
      season: contextSeason, competition: contextCompetition, group: contextGroup,
    })}</p>}
  </>;
  const searchModes = !similarityPage && !comparisonPage && <div className="dr-toolbar">
    <ToggleButtonGroup className="dr-player-search-modes" exclusive value={globalSearch ? 'global' : 'context'} aria-label={d('globalPlayers.mode')}
      onChange={(_, value) => {
        if (!value) return;
        setSelectedPlayerId(null);
        update({ searchScope: value === 'global' ? 'global' : null, page: null, player: null });
      }}>
      <ToggleButton value="context" disabled={!full}>{d('globalPlayers.contextMode')}</ToggleButton>
      <ToggleButton value="global" disabled={!full}>{d('globalPlayers.globalMode')}</ToggleButton>
    </ToggleButtonGroup>
  </div>;
  if (globalSearch) return <DirectoryPage className="dr-ranking" title={d('playersTitle')}
    subtitle={d('globalPlayers.subtitle')}>
    {searchModes}
    <PlayerGlobalSearch query={query} onChange={value => update({ q: value })}
      page={params.get('page')} onPageChange={value => update({ page: String(value) })} />
  </DirectoryPage>;
  return <DirectoryPage className={`dr-ranking${showingComparison ? ' pc-page' : ''}`}
    title={d(showingSimilar ? 'discovery.similarTitle' : showingComparison ? 'comparePlayers' : 'playersTitle')}
    subtitle={d(showingSimilar ? 'discovery.similarIntro' : showingComparison ? 'comparison.intro' : 'playersSubtitle')} actions={!showingSimilar && !showingComparison &&
    <Button onClick={exportExcel} disabled={!full || !players.length} startIcon={<Download />}>{t('players.exportExcel')}</Button>
  }>
    {searchModes}
    {(showingSimilar || showingComparison) && <Button component={Link} to={`/rankingJugadores?${rankingParams}`}>
      {d('discovery.backToRanking')}
    </Button>}
    {!showingComparison && <section className="pm-filter-panel" data-tour="ranking-players-filters">{contextControls}</section>}
    {!showingComparison && <DirectorySavedSearches scope="players" context={filters} onRestore={setFilters} disabled={!full} />}
    {!showingComparison && error && <Alert severity="error" action={<Button onClick={retry}>{t('players.errors.retry')}</Button>}>{t('players.errors.loadData')}</Alert>}
    {!showingComparison && <div className="dr-panel dr-toolbar" data-tour="ranking-players-search">
      <TextField className="dr-search" label={d('searchPlayer')} placeholder={t('players.search.placeholder')}
        value={query} onChange={(event) => update({ q: event.target.value })} disabled={!full} />
      <TextField select label={t('players.filters.team.label')} value={selectedTeamName} disabled={!full}
        onChange={(event) => update({ team: event.target.value, teamId: null })}>
        <MenuItem value="">{t('players.filters.team.all')}</MenuItem>
        {selectedTeamName && !teams.includes(selectedTeamName) && <MenuItem value={selectedTeamName}>{selectedTeamName}</MenuItem>}
        {teams.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <TextField select label={t('players.filters.position.label')} value={position} disabled={!full}
        helperText={source.length > 0 && positions.length === 0 ? d('discovery.positionMissing') : undefined}
        onChange={(event) => update({ position: event.target.value })}>
        <MenuItem value="">{t('players.filters.position.all')}</MenuItem>
        {position && position !== '__unknown__' && !positions.includes(position) && <MenuItem value={position}>{position}</MenuItem>}
        {positions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        {source.some(player => !playerPosition(player)) && <MenuItem value="__unknown__">{d('discovery.positionUnknown')}</MenuItem>}
      </TextField>
      <TextField label={d('minimumMinutes')} type="number" inputProps={{ min: 0, step: 1 }} disabled={!full}
        value={full ? params.get('minMinutes') || '' : ''} placeholder="0"
        onChange={(event) => update({ minMinutes: event.target.value })} />
    </div>}
    {showingSimilar && <details className="dr-panel pd-filter-toggle">
      <summary>{d('discovery.filterAction')}</summary>
      <PlayerDiscoveryControls compact params={params} filters={discovery} sources={sources} sort={sort} onChange={update} />
    </details>}
    {!showingComparison && needsSource && <Alert severity="info">{d('discovery.sourceRequired')}</Alert>}
    {!showingComparison && full && discovery.invalid && <Alert severity="warning" action={<Button onClick={() =>
      update(Object.fromEntries(DISCOVERY_QUERY_KEYS.map(key => [key, null])))}>{d('discovery.clear')}</Button>}>
      {d('discovery.invalidFilters')}
    </Alert>}
    {showingSimilar && !loading && !error && <PlayerSimilarity records={source} candidates={players} params={params} onChange={update}
      profileUrl={profileUrl} profileState={profileState} onCompare={showComparison} />}
    {showingComparison && <PlayerComparisonWorkspace initialContext={comparisonContext} minimum={minimum}
      profileUrl={profileUrl} profileState={profileState} />}
    {!showingSimilar && !showingComparison && <>
    <div className="dr-toolbar">
      {full && <>
        <Button aria-pressed={sort === 'd_rating'} variant={sort === 'd_rating' ? 'contained' : 'text'}
          onClick={() => update({ sort: 'd_rating', direction: 'desc', metricMatches: '3', metricMinutes: '450' })}>{d('discovery.ratingPreset')}</Button>
        <Button aria-pressed={sort === 'd_nonPenaltyGoals90'} variant={sort === 'd_nonPenaltyGoals90' ? 'contained' : 'text'}
          onClick={() => update({ sort: 'd_nonPenaltyGoals90', direction: 'desc', minMinutes: '450' })}>{d('discovery.goalsPreset')}</Button>
      </>}
      <ToggleButtonGroup aria-label={t('players.categoryLabel')} value={category} exclusive onChange={(_, value) => value &&
        update({ category: value, sort: value === 'all' || value === 'goles' ? 'golesTotales' : value, direction: 'desc', details: null })}>
        {[['all', 'all'], ['goles', 'goals'], ['minutosJugados', 'minutes'], ['puntosDisciplinarios', 'cards']].map(([value, label]) =>
          <ToggleButton key={value} value={value} disabled={!full}>{t(`players.categories.${label}`)}</ToggleButton>)}
      </ToggleButtonGroup>
      {!mobile && <ToggleButtonGroup exclusive value={cards ? 'cards' : 'table'} onChange={(_, value) => value && update({ view: value })}>
        <ToggleButton value="table">{d('table')}</ToggleButton><ToggleButton value="cards">{d('cards')}</ToggleButton>
      </ToggleButtonGroup>}
      {full && (query || team || teamId || position || minimum > 0 || sort.startsWith('d_')
        || DISCOVERY_QUERY_KEYS.some(key => params.has(key))) && <Button onClick={() => update({
        ...Object.fromEntries(['q', 'team', 'teamId', 'position', 'minMinutes', ...DISCOVERY_QUERY_KEYS].map(key => [key, null])),
        ...(sort.startsWith('d_') ? { sort: null, direction: null } : {}),
      })}>{d('clear')}</Button>}
    </div>
    {cards && <div className="dr-panel dr-toolbar">
      <TextField select label={d('sort')} value={sort} disabled={!full}
        onChange={(event) => update({ sort: event.target.value })}>
        <MenuItem value="nombre">{t('players.table.player')}</MenuItem>
        {availableMetrics.map(([key, label]) => <MenuItem key={key} value={key}>{metricLabel(key, label)}</MenuItem>)}
      </TextField>
      <TextField select label={d('direction')} value={direction} disabled={!full}
        onChange={(event) => update({ direction: event.target.value })}>
        <MenuItem value="desc">{d('descending')}</MenuItem><MenuItem value="asc">{d('ascending')}</MenuItem>
      </TextField>
    </div>}
    <div className={`dr-layout${selected ? '' : ' pd-ranking-full'}`}>
      <section className="dr-panel" aria-label={rankingTitle}>
        <div className="dr-panel-header"><div><h2>{rankingTitle}</h2><p>{t('players.playersFound', { count: players.length })}</p></div>
          <Button onClick={() => {
            setColumns((details ? categoryMetrics : availableMetrics).map(([key]) => key));
            update({ details: details ? null : 'all' });
          }} aria-pressed={details}>{d('moreStats')}</Button></div>
        {full && <details className="pd-filter-toggle">
          <summary>{d('discovery.filterAction')}{DISCOVERY_QUERY_KEYS.some(key => params.has(key)) && ` · ${d('discovery.filtersApplied')}`}</summary>
          <PlayerDiscoveryControls compact params={params} filters={discovery} sources={sources} sort={sort} onChange={update} />
        </details>}
        <DirectoryColumns options={availableMetrics.map(([key, label]) => [key, metricLabel(key, label)])} value={columns}
          onChange={setColumns} disabled={!full} />
        {loading && <div role="status" aria-label={d('loading')}>{[1, 2, 3].map((key) => <Skeleton key={key} height={64} />)}</div>}
        {!loading && !players.length && <p className="dr-empty">{t('players.emptyState.title')}</p>}
        {!loading && Boolean(players.length) && <PlayerRankingResults players={visible}
          metrics={shownMetrics.map(([key, label]) => [key, metricLabel(key, label)])} title={rankingTitle}
          sort={sort} direction={direction} onSort={sortBy} onSelect={setSelectedPlayerId} selectedId={selectedId}
          profileUrl={profileUrl} profileState={profileState} displayMetric={displayMetric} cards={cards} full={full}
          offset={(page - 1) * pageSize} />}
        {!loading && full && <DirectoryPagination page={page} pageSize={pageSize} total={players.length}
          onChange={(value) => update({ page: String(value), player: null })} />}
        {!loading && !full && players.length > 5 && <LicensePreviewNotice permission="analytics.view" visibleCount={5} />}
        {!full && <p className="dr-muted">{d('previewHint')}</p>}
      </section>
      {selected && !loading && <aside id="ranking-selected-player" tabIndex={-1}
        className="dr-panel dr-profile" aria-label={d('selectedPlayer')}>
        <p className="dr-muted">{d('selectedPlayer')}</p>
        <div className="pd-selected-heading"><h2>{playerName(selected)}</h2>{follow(selected)}</div>
        <p>{selected.nombreEquipo || selected.equipo} {playerPosition(selected) && `· ${playerPosition(selected)}`}</p>
        <dl className="dr-kpis">{categoryMetrics.map(([key, label]) => <div key={key}><dt>{metricLabel(key, label)}</dt><dd>{format(selected, key)}</dd></div>)}</dl>
        <div className="dr-profile-actions pd-profile-actions">
          <Button className="pd-profile-primary" component={Link} to={profileUrl(selected)} state={profileState(selected)}
            variant="contained" endIcon={<ArrowForward />} aria-label={d('profile')}>{d('discovery.profileShort')}</Button>
          {full && <Button variant="outlined" onClick={() => showComparison(selected)}
            aria-label={d('comparePlayers')}>{d('discovery.compareShort')}</Button>}
          {full && <Button variant="outlined" onClick={() => showSimilar(selected)}
            aria-label={d('discovery.similarAction')}>{d('discovery.similarShort')}</Button>}
          <Button className="pd-profile-close" onClick={() => {
            document.getElementById(`ranking-player-${selectedId}`)?.focus();
            setSelectedPlayerId(null);
            update({ player: null });
          }}>{d('close')}</Button>
        </div><small>{d('profileHint')}</small>
      </aside>}
    </div>
    </>}
    <p className="dr-note">{d('contextHint')}</p>
  </DirectoryPage>;
}
